/*
 * DEBUG MODE REMOVAL GUIDE FOR PRODUCTION:
 *
 * To remove debug mode completely for production users:
 *
 * 1. Remove debug imports (lines ~55-57):
 *    - Delete: import DebugToggle from '../components/DebugToggle';
 *    - Delete: import { DEBUG_MODE } from '../helpers/constants';
 *    - Delete: import { getDebugAutomationStats, getDebugRecentActions, getDebugAIInsights } from '../helpers/debugData';
 *
 * 2. Remove debug component (lines ~437-440):
 *    - Delete the entire <DebugToggle> component block
 *
 * 3. Remove debug queries (lines ~134-173):
 *    - Remove all DEBUG_MODE conditions from useQuery hooks
 *    - Remove debug data functions from queryFn
 *    - Remove DEBUG_MODE from queryKey arrays
 *
 * 4. Remove debug data processing (lines ~186-195):
 *    - Delete console.log statements
 *    - Remove debug data structure handling
 *
 * 5. Remove debug conditional fields (lines ~286-291):
 *    - Remove all DEBUG_MODE checks from action processing
 *    - Keep only the real data processing logic
 *
 * 6. Delete debug files:
 *    - Delete: src/components/DebugToggle.jsx
 *    - Delete: src/helpers/debugData.js
 *    - Remove DEBUG_MODE from src/helpers/constants.js
 *
 * 7. Remove debug mode from Repairs page:
 *    - Follow same pattern in src/pages/Repairs.jsx
 *
 * All debug-related code is clearly marked with comments for easy identification.
 */

import React, { useContext, useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import {
  Card,
  Button,
  Tag,
  Row,
  Col,
  List,
  Avatar,
  Progress,
  Alert,
  Badge,
  Timeline,
  message,
  Tooltip,
  Link,
  Spin,
} from "antd";
import {
  MessageOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  DollarOutlined,
  FileTextOutlined,
  UserOutlined,
  ToolOutlined,
  SafetyOutlined,
  BellOutlined,
  WalletOutlined,
  SearchOutlined,
  CalendarOutlined,
  BankOutlined,
  SettingOutlined,
  InfoCircleOutlined,
  BarChartOutlined,
  ThunderboltOutlined,
  ArrowLeftOutlined,
  HourglassOutlined,
  ClockCircleFilled,
  CheckCircleFilled,
  BulbOutlined,
} from "@ant-design/icons";
import { AuthContext } from "../context/AuthProvider";
import { device, LAYOUT_CONSTANTS, useBreakpoints } from "../lib/breakpoints";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import SettingsModel from "../lib/firebase/settingsModel";
import aiAutomationModel from "../lib/firebase/aiAutomationModel";
import { leoActionsModel } from "../lib/firebase/leoActionsModel";
import { leoActionsKeys } from "../lib/queryKeys";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { listTenants } from "../lib/firebase/tenantModel";
import unitModel from "../lib/firebase/unitModel";
import { getUnitDisplayAddress } from "../lib/unitAddressUtils";
import { propertyKeys } from "../lib/queryKeys";
import PageHeader from "../components/PageHeader";
import { StatCard } from "../components/shared";
// DEBUG MODE IMPORTS - Remove these lines for production:
import { getDebugMode } from "../helpers/constants";
import {
  getDebugAutomationStats,
  getDebugRecentActions,
  getDebugAIInsights,
} from "../helpers/debugData";
import Logo from "../components/general/Logo";
import pluralize from "pluralize";

// Helper function to get icon component from string name
const getIconComponent = (iconName) => {
  console.log("[AI Dashboard] getIconComponent called with:", iconName);
  const iconMap = {
    MessageOutlined,
    ClockCircleOutlined,
    ToolOutlined,
    DollarOutlined,
    BankOutlined,
    SafetyOutlined,
    FileTextOutlined,
    ExclamationCircleOutlined,
    InfoCircleOutlined,
    UserOutlined,
    BarChartOutlined,
    ThunderboltOutlined,
    BellOutlined,
    WalletOutlined,
    SearchOutlined,
    CalendarOutlined,
  };
  const result = iconMap[iconName] || InfoCircleOutlined;
  console.log("[AI Dashboard] getIconComponent returning:", result);
  return result;
};

const isUnknownProperty = (value) =>
  !value ||
  ["unknown property", "unknown address", "unknown door", "unknown unit"].includes(
    String(value).trim().toLowerCase()
  );

// Helper function to get category icon
const getCategoryIcon = (category) => {
  switch (category) {
    case "Rent Reminders":
      return <BellOutlined />;
    case "Maintenance Coordination":
      return <ToolOutlined />;
    case "Late Fee Processing":
      return <ExclamationCircleOutlined />;
    case "Deposit Processing":
      return <WalletOutlined />;
    case "Screening":
      return <SearchOutlined />;
    case "Communication":
      return <MessageOutlined />;
    case "Other":
      return <InfoCircleOutlined />;
    default:
      return <MessageOutlined />;
  }
};

/** Trim long Leo / landlord Q&A for the activity timeline */
const truncateAssistText = (text, maxLen) => {
  if (!text || typeof text !== "string") return "";
  const t = text.trim();
  if (!t) return "";
  if (t.length <= maxLen) return t;
  return `${t.slice(0, Math.max(0, maxLen - 1)).trimEnd()}…`;
};

/** Collapsed Leo reply: first line only, capped (expand for full message). */
const LEO_ASSIST_COLLAPSED_CHARS = 180;

const getLeoAssistCollapsedPreview = (text) => {
  if (!text || typeof text !== "string") return "";
  const trimmed = text.trim();
  if (!trimmed) return "";
  const firstLine = (trimmed.split(/\n/)[0] || "").trim() || trimmed;
  return truncateAssistText(firstLine, LEO_ASSIST_COLLAPSED_CHARS);
};

const leoAssistReplyHasExpandable = (text) => {
  if (!text || typeof text !== "string") return false;
  const t = text.trim();
  if (!t) return false;
  if (t.includes("\n")) return true;
  return t.length > LEO_ASSIST_COLLAPSED_CHARS;
};

/** Render Leo's lightweight markdown without exposing raw ** markers. */
const renderAssistMessage = (text) => {
  if (!text || typeof text !== "string") return null;

  return text.split("\n").map((line, lineIndex, lines) => (
    <React.Fragment key={`${lineIndex}-${line.slice(0, 16)}`}>
      {line.split(/(\*\*[^*]+\*\*)/g).map((part, partIndex) =>
        part.startsWith("**") && part.endsWith("**") ? (
          <strong key={partIndex}>{part.slice(2, -2)}</strong>
        ) : (
          <React.Fragment key={partIndex}>{part}</React.Fragment>
        )
      )}
      {lineIndex < lines.length - 1 ? <br /> : null}
    </React.Fragment>
  ));
};

// Helper function to format time saved
const formatTimeSaved = (minutes) => {
  if (minutes < 60) {
    return `${minutes}m`;
  } else if (minutes < 1440) {
    // Less than 24 hours
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0
      ? `${hours}h ${remainingMinutes}m`
      : `${hours}h`;
  } else {
    const days = Math.floor(minutes / 1440);
    const remainingHours = Math.floor((minutes % 1440) / 60);
    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
  }
};

// Leo sparkle for PageHeader: same asset as nav; avoids wide wordmark padding in logo.png
const LogoIcon = () => (
  <img
    src="/icons/leo/leo.png"
    alt=""
    aria-hidden
    style={{
      width: "1.5rem",
      height: "1.5rem",
      flexShrink: 0,
      objectFit: "contain",
      objectPosition: "left center",
      filter: "brightness(0) invert(1)",
      display: "block",
    }}
  />
);
// Mark this as a custom logo component
LogoIcon.isCustomLogo = true;

const AIAutomationDashboard = () => {
  const { isMobile } = useBreakpoints();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showLeoGuide, setShowLeoGuide] = useState(false);
  const [expandedLeoAssistIds, setExpandedLeoAssistIds] = useState(
    () => new Set()
  );

  const { user, userInfo } = useContext(AuthContext);

  // Debug mode - automatically determined by route
  const isDebugMode = getDebugMode();

  // Load data using React Query for better caching and performance or debug data
  const {
    data: automationStats = {
      messagesSent: 0,
      remindersSent: 0,
      maintenanceRequests: 0,
      lateFeesApplied: 0,
      depositsProcessed: 0,
      screeningCompleted: 0,
    },
    isLoading: isStatsLoading,
  } = useQuery({
    // DEBUG MODE QUERY - Remove debug conditions for production:
    queryKey: leoActionsKeys.stats(user?.uid, isDebugMode),
    queryFn: () => {
      if (isDebugMode) {
        return Promise.resolve(getDebugAutomationStats());
      }
      return leoActionsModel.getActionStats();
    },
    enabled: !!user?.uid || isDebugMode,
  });

  // DEBUG MODE QUERY - Remove debug conditions for production:
  const { data: recentActionsRaw = [], isLoading: isRecentLoading } = useQuery({
    queryKey: leoActionsKeys.recent(user?.uid, isDebugMode),
    queryFn: () => {
      if (isDebugMode) {
        return Promise.resolve(getDebugRecentActions());
      }
      return leoActionsModel.getRecentActions(10);
    },
    enabled: !!user?.uid || isDebugMode,
  });

  const { data: userSettings, isLoading: isSettingsLoading } = useQuery({
    queryKey: leoActionsKeys.user(user?.uid),
    queryFn: () => SettingsModel.getOrCreateSettings(),
    enabled: !!user?.uid,
  });

  // DEBUG MODE QUERY - Remove DEBUG_MODE conditions for production:
  const { data: aiInsights = [], isLoading: isInsightsLoading } = useQuery({
    queryKey: leoActionsKeys.user(user?.uid, isDebugMode),
    queryFn: () => {
      if (isDebugMode) {
        return Promise.resolve(getDebugAIInsights());
      }
      return aiAutomationModel.getAIInsights();
    },
    enabled: !!user?.uid || isDebugMode,
  });

  // Load tenants and units data for lookup
  const { data: tenants = [], isLoading: isTenantsLoading } = useQuery({
    queryKey: propertyKeys.tenants(user?.uid),
    queryFn: () => listTenants(),
    enabled: !!user?.uid,
  });

  const { data: units = [], isLoading: isUnitsLoading } = useQuery({
    queryKey: propertyKeys.units(user?.uid),
    queryFn: () => unitModel.getByOwner(user.uid),
    enabled: !!user?.uid,
  });

  // Process the raw data with mobile-friendly formatting
  // DEBUG MODE LOGGING - Remove these lines for production:
  console.log("[AI Dashboard] isDebugMode:", isDebugMode);
  console.log("[AI Dashboard] recentActionsRaw:", recentActionsRaw);

  // Calculate start of this week (Monday) to determine which actions are from last week
  const now = new Date();
  const startOfWeek = new Date(now);
  const dayOfWeek = now.getDay();
  const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  startOfWeek.setDate(now.getDate() - daysToSubtract);
  startOfWeek.setHours(0, 0, 0, 0);

  const recentActions = recentActionsRaw.map((action) => {
    // DEBUG MODE DATA HANDLING - Remove this block for production:
    if (isDebugMode && action.action && action.mobileAction) {
      console.log("[AI Dashboard] Using debug data for action:", action);
      return action; // Debug data is already processed
    }

    const actionType =
      action.actionData?.actionType || action.actionType || "unknown";
    const rawDescription =
      action.description ||
      action.actionData?.description ||
      "Action completed";
    // Replace raw date strings embedded in descriptions with friendly dates.
    // Handles both "2026-06-08T12:00:00.000Z" and bare "2026-03-17" forms.
    const description =
      typeof rawDescription === "string"
        ? rawDescription.replace(
            /\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z?)?/g,
            (match) => {
              try {
                // Force UTC interpretation so we always display the stored calendar
                // date regardless of the viewer's local timezone offset.
                const iso = match.includes("T") ? match : `${match}T00:00:00Z`;
                const d = new Date(iso);
                if (isNaN(d.getTime())) return match;
                return d.toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                  timeZone: "UTC",
                });
              } catch {
                return match;
              }
            }
          )
        : rawDescription;

    // Try to look up tenant and unit information if missing
    let tenantName = action.entityName;
    let unitAddress = action.propertyAddress;

    // If tenant or unit info is missing, try to find it from action data
    if (
      (!tenantName ||
        tenantName === "Unknown Entity" ||
        tenantName === "Unknown Tenant") &&
      (!unitAddress ||
        unitAddress === "Unknown Address" ||
        unitAddress === "Unknown Door")
    ) {
      // Look for tenant ID or unit ID in action data
      const tenantId =
        action.actionData?.parameters?.tenantId ||
        action.actionData?.target?.tenantId;
      const unitId =
        action.actionData?.parameters?.unitId ||
        action.actionData?.target?.unitId;

      if (tenantId && tenants.length > 0) {
        const tenant = tenants.find((t) => t.id === tenantId);
        if (tenant) {
          tenantName = tenant.name;

          // If we found a tenant, try to get their unit address
          if (tenant.unitId && units.length > 0) {
            const unit = units.find((u) => u.id === tenant.unitId);
            if (unit) {
              unitAddress = getUnitDisplayAddress(unit) || unit.name;
            }
          }
        }
      } else if (unitId && units.length > 0) {
        const unit = units.find((u) => u.id === unitId);
        if (unit) {
          unitAddress = getUnitDisplayAddress(unit) || unit.name;

          // Try to find tenant for this unit
          if (tenants.length > 0) {
            const tenant = tenants.find((t) => t.unitId === unitId);
            if (tenant) {
              tenantName = tenant.name;
            }
          }
        }
      }
    }

    // Create desktop-friendly action text with more details
    let desktopAction = description;
    let mobileAction = description;
    let assistQuestionDisplay = "";
    let assistAnswerDisplay = "";

    if (description.includes("Rent reminder sent")) {
      desktopAction = description; // Keep full description for desktop
      mobileAction = "Rent reminder sent";
    } else if (description.includes("Message sent")) {
      desktopAction = description;
      mobileAction = "Message sent";
    } else if (description.includes("Maintenance")) {
      desktopAction = description;
      mobileAction = "Maintenance coordinated";
    } else if (description.includes("Late fee")) {
      desktopAction = description;
      mobileAction = "Late fee applied";
    } else if (description.includes("Deposit")) {
      desktopAction = description;
      mobileAction = "Deposit processed";
    } else if (description.includes("Screening")) {
      desktopAction = description;
      mobileAction = "Screening completed";
    } else if (
      actionType === "ai_assistance" ||
      (typeof description === "string" &&
        description.includes("AI provided property management assistance"))
    ) {
      const fromContextQ = (action.aiContext?.userMessage || "").trim();
      const fromContextA = (action.aiContext?.aiResponse || "").trim();
      let questionFromDesc = "";
      if (
        typeof description === "string" &&
        description.includes("AI provided property management assistance")
      ) {
        const match = description.match(
          /AI provided property management assistance:\s*(.+)/s
        );
        if (match) {
          questionFromDesc = match[1].trim();
          if (questionFromDesc.endsWith("...")) {
            questionFromDesc = questionFromDesc.slice(0, -3).trimEnd();
          }
        }
      }
      const assistQuestion = fromContextQ || questionFromDesc;
      const assistAnswer = fromContextA;

      const qShort = truncateAssistText(assistQuestion, 72);
      const headline =
        assistQuestion && qShort
          ? `You asked: ${qShort}`
          : "Property management help";
      desktopAction = headline;
      mobileAction = assistQuestion
        ? `You: ${truncateAssistText(assistQuestion, 64)}`
        : headline;

      assistQuestionDisplay = assistQuestion;
      assistAnswerDisplay = assistAnswer;
    }

    return {
      id: action.id,
      type: actionType,
      action: desktopAction,
      mobileAction: mobileAction,
      assistQuestion: assistQuestionDisplay,
      assistAnswer: assistAnswerDisplay,
      tenant: tenantName || "Unknown Tenant",
      unit: unitAddress || "Unknown Door",
      // DEBUG MODE CONDITIONAL FIELDS - Remove DEBUG_MODE checks for production:
      time: isDebugMode
        ? action.time
        : leoActionsModel.formatTimeAgo(action.createdAt),
      actionDate: isDebugMode
        ? action.actionDate || null
        : leoActionsModel.formatActionDate(action.createdAt),
      status: isDebugMode
        ? action.status
        : action.result?.data?.status || "completed",
      icon: isDebugMode
        ? action.icon
        : leoActionsModel.getActionIcon(actionType),
      details: isDebugMode ? action.details : action.result?.data || null,
      timeSaved: isDebugMode
        ? action.timeSaved
        : leoActionsModel.getTimeSavedPerAction(actionType, description),
      // Determine if action is from last week (before start of this week)
      isLastWeek: (() => {
        if (isDebugMode) {
          // For debug mode, check if timeSaved is 0 (not counted in this week's stats)
          return action.timeSaved === 0 || !action.timeSaved;
        }
        if (!action.createdAt) return false;
        const actionDate = action.createdAt.toDate
          ? action.createdAt.toDate()
          : new Date(action.createdAt);
        return actionDate < startOfWeek;
      })(),
    };
  });

  const toggleLeoAssistExpanded = (actionId) => {
    if (actionId == null) return;
    setExpandedLeoAssistIds((prev) => {
      const next = new Set(prev);
      if (next.has(actionId)) next.delete(actionId);
      else next.add(actionId);
      return next;
    });
  };

  const renderLeoAssistTimelineBody = (action) => {
    const fullAnswer = (action.assistAnswer || "").trim();
    const expanded = action.id != null && expandedLeoAssistIds.has(action.id);
    const hasExpandableLeo = leoAssistReplyHasExpandable(fullAnswer);
    const collapsedPreview = getLeoAssistCollapsedPreview(fullAnswer);

    return (
      <TimelineAssistWrap>
        {action.assistQuestion ? (
          <TimelineAssistBlock $role="user">
            <TimelineAssistLabel $role="user">You</TimelineAssistLabel>
            <TimelineAssistText $role="user">
              {renderAssistMessage(
                truncateAssistText(action.assistQuestion, 360)
              )}
            </TimelineAssistText>
          </TimelineAssistBlock>
        ) : null}
        {fullAnswer ? (
          <TimelineAssistBlock $role="leo">
            <TimelineAssistLabel $role="leo">Leo AI</TimelineAssistLabel>
            <TimelineAssistText $role="leo">
              {renderAssistMessage(
                expanded || !hasExpandableLeo
                  ? action.assistAnswer
                  : collapsedPreview
              )}
            </TimelineAssistText>
            {hasExpandableLeo ? (
              <LeoAssistExpandBtn
                type="button"
                onClick={() => toggleLeoAssistExpanded(action.id)}
                aria-expanded={expanded}
              >
                {expanded ? "Show less" : "Show full answer"}
              </LeoAssistExpandBtn>
            ) : null}
          </TimelineAssistBlock>
        ) : null}
      </TimelineAssistWrap>
    );
  };

  const loading =
    isStatsLoading ||
    isRecentLoading ||
    isSettingsLoading ||
    isInsightsLoading ||
    isTenantsLoading ||
    isUnitsLoading;

  const handleActionClick = async (actionId, actionType, actionData) => {
    try {
      await aiAutomationModel.executeAction(actionId, actionType, actionData);
      message.success(`Leo is handling: ${actionData.action || actionType}`);

      // Invalidate React Query cache to refresh data
      queryClient.invalidateQueries({
        queryKey: leoActionsKeys.stats(user?.uid),
      });
      queryClient.invalidateQueries({
        queryKey: leoActionsKeys.recent(user?.uid),
      });
    } catch (error) {
      console.error("Error executing action:", error);
      message.error(`Failed to execute action: ${error.message}`);
    }
  };

  const handleAIAction = async (insightId, insightType, actionData) => {
    try {
      await aiAutomationModel.executeAction(insightId, insightType, actionData);
      message.success(`Leo is working on: ${actionData.action || insightType}`);

      // Invalidate React Query cache to refresh insights
      queryClient.invalidateQueries({
        queryKey: leoActionsKeys.user(user?.uid),
      });
    } catch (error) {
      console.error("Error executing AI action:", error);
      message.error(`Failed to execute AI action: ${error.message}`);
    }
  };

  const handleQuickAction = async (actionType, actionData) => {
    try {
      await aiAutomationModel.executeAction(null, actionType, actionData);
      message.success(`Leo is executing: ${actionData.action || actionType}`);

      // Invalidate React Query cache to refresh stats
      queryClient.invalidateQueries({
        queryKey: leoActionsKeys.stats(user?.uid),
      });
    } catch (error) {
      console.error("Error executing quick action:", error);
      message.error(`Failed to execute quick action: ${error.message}`);
    }
  };

  if (loading) {
    return (
      <Container className="ai-automation-dashboard">
        <PageHeader
          title="Leo AI"
          subtitle="Your intelligent AI property management assistant"
          icon={LogoIcon}
          settingsLink={true}
        />

        <LoadingContainer>
          <LoadingSpinner />
          <LoadingText>Loading your automation dashboard...</LoadingText>
        </LoadingContainer>
      </Container>
    );
  }

  // Check if Leo has done anything yet
  console.log("[AI Dashboard] Checking hasAnyActivities with:", {
    automationStats,
    recentActionsLength: recentActions.length,
    aiInsightsLength: aiInsights?.length || 0,
  });

  const hasAnyActivities =
    automationStats.messagesSent > 0 ||
    automationStats.remindersSent > 0 ||
    automationStats.maintenanceRequests > 0 ||
    automationStats.lateFeesApplied > 0 ||
    automationStats.depositsProcessed > 0 ||
    automationStats.screeningCompleted > 0 ||
    recentActions.length > 0 ||
    (aiInsights && aiInsights.length > 0);

  console.log("[AI Dashboard] hasAnyActivities result:", hasAnyActivities);

  // Empty state when Leo hasn't done anything yet
  if (!hasAnyActivities) {
    return (
      <Container className="ai-automation-dashboard">
        <PageHeader
          title="Leo AI"
          subtitle="Your intelligent AI property management assistant"
          icon={LogoIcon}
          settingsLink={true}
        />

        <EmptyStateContainer>
          <EmptyStateTitle>Meet Leo</EmptyStateTitle>
          <EmptyStateDescription>
            Leo is your intelligent, AI property management assistant, ready to
            help automate your daily tasks. To get started, make sure Leo has
            the permissions it needs to work effectively.
          </EmptyStateDescription>

          <EmptyStateActions>
            <EmptyStateButton
              type="primary"
              size="large"
              onClick={() => navigate("/settings")}
            >
              <SettingOutlined />
              Configure Leo Permissions
            </EmptyStateButton>
          </EmptyStateActions>

          <EmptyStateFeatures>
            <EmptyStateFeatureTitle>
              What Leo can do for you:
            </EmptyStateFeatureTitle>
            <EmptyStateFeatureList>
              <EmptyStateFeatureItem>
                <MessageOutlined />
                <span>Send automated rent reminders</span>
              </EmptyStateFeatureItem>
              <EmptyStateFeatureItem>
                <ClockCircleOutlined />
                <span>Coordinate maintenance requests</span>
              </EmptyStateFeatureItem>
              <EmptyStateFeatureItem>
                <DollarOutlined />
                <span>Apply late fees automatically</span>
              </EmptyStateFeatureItem>
              <EmptyStateFeatureItem>
                <SafetyOutlined />
                <span>Screen new applicants</span>
              </EmptyStateFeatureItem>
              <EmptyStateFeatureItem>
                <FileTextOutlined />
                <span>Generate monthly reports</span>
              </EmptyStateFeatureItem>
              <EmptyStateFeatureItem>
                <BankOutlined />
                <span>Process security deposits</span>
              </EmptyStateFeatureItem>
            </EmptyStateFeatureList>
          </EmptyStateFeatures>
        </EmptyStateContainer>
      </Container>
    );
  }

  return (
    <Container className="ai-automation-dashboard">
      <PageHeader
        title="Leo AI"
        subtitle="Your intelligent AI property management assistant"
        icon={LogoIcon}
        settingsLink={true}
      />

      {/* Automation Overview */}
      <StatsSection>
        <Row gutter={[24, 24]}>
          <Col xs={24} sm={12} lg={6}>
            <Tooltip
              title="Number of automated messages Leo has sent to tenants (rent reminders, notifications, etc.)"
              placement="top"
            >
              <div>
                <StatCard
                  className="stats-card"
                  title="Messages Sent"
                  value={automationStats.messagesSent}
                  prefix={
                    <MessageOutlined
                      style={{ color: "var(--color-primary)" }}
                    />
                  }
                  valueStyle={{ color: "var(--color-primary)" }}
                />
              </div>
            </Tooltip>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Tooltip
              title="Number of rent reminders and payment notifications Leo has automatically sent to tenants"
              placement="top"
            >
              <div>
                <StatCard
                  className="stats-card"
                  title="Reminders Sent"
                  value={automationStats.remindersSent}
                  prefix={
                    <ClockCircleOutlined style={{ color: "#faad14" }} />
                  }
                  valueStyle={{ color: "#faad14" }}
                />
              </div>
            </Tooltip>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Tooltip
              title="Number of maintenance requests Leo has coordinated and scheduled with contractors"
              placement="top"
            >
              <div>
                <StatCard
                  className="stats-card"
                  title="Maintenance Coordinated"
                  value={automationStats.maintenanceRequests}
                  prefix={
                    <ToolOutlined style={{ color: "var(--color-primary)" }} />
                  }
                  valueStyle={{ color: "var(--color-primary)" }}
                />
              </div>
            </Tooltip>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Tooltip
              title="Number of late fees Leo has automatically applied to overdue rent payments"
              placement="top"
            >
              <div>
                <StatCard
                  className="stats-card"
                  title="Late Fees Applied"
                  value={automationStats.lateFeesApplied}
                  prefix={
                    <DollarOutlined
                      style={{ color: "var(--color-accent)" }}
                    />
                  }
                  valueStyle={{ color: "var(--color-accent)" }}
                />
              </div>
            </Tooltip>
          </Col>
        </Row>
      </StatsSection>

      {/* What Leo Did Recently */}
      <SectionCard
        title={isMobile ? `Leo's Activity` : `What Leo Did Recently`}
        className="section-card"
        extra={
          <Tooltip title="Learn about Leo's capabilities" placement="left">
            <LeoInfoButton onClick={() => setShowLeoGuide(true)}>
              <InfoCircleOutlined
                style={{ color: "white", filter: "brightness(0) invert(1)" }}
              />
            </LeoInfoButton>
          </Tooltip>
        }
      >
        {/* Time Saved Summary - Integrated with weekly actions */}
        {(() => {
          // Calculate total time saved and breakdown from visible this week actions to match what's shown in the list
          const thisWeekActions = recentActions.filter(
            (action) => !action.isLastWeek
          );
          const visibleTimeSaved = thisWeekActions.reduce(
            (sum, action) => sum + (action.timeSaved || 0),
            0
          );
          const displayTimeSaved =
            visibleTimeSaved > 0
              ? visibleTimeSaved
              : automationStats.totalTimeSaved;

          // Calculate breakdown from visible actions
          const calculateCategory = (action) => {
            const actionType = action.type || "";
            const description = (action.action || "").toLowerCase();

            if (
              actionType === "reminder_sent" ||
              actionType === "reminder" ||
              description.includes("reminder")
            ) {
              return "Rent Reminders";
            } else if (
              actionType.includes("maintenance") ||
              description.includes("maintenance")
            ) {
              return "Maintenance Coordination";
            } else if (
              actionType.includes("late_fee") ||
              description.includes("late fee")
            ) {
              return "Late Fee Processing";
            } else if (
              actionType.includes("deposit") ||
              description.includes("deposit")
            ) {
              return "Deposit Processing";
            } else if (
              actionType.includes("screening") ||
              description.includes("screening")
            ) {
              return "Screening";
            } else if (
              actionType === "message_sent" ||
              actionType === "message" ||
              description.includes("message") ||
              description.includes("inquiry") ||
              description.includes("assisted")
            ) {
              return "Comms";
            } else {
              return "Other";
            }
          };

          const visibleBreakdown = thisWeekActions.reduce(
            (breakdown, action) => {
              const category = calculateCategory(action);
              const timeSaved = action.timeSaved || 0;
              breakdown[category] = (breakdown[category] || 0) + timeSaved;
              return breakdown;
            },
            {}
          );

          // Use visible breakdown if we have visible actions, otherwise fall back to stats breakdown
          const displayBreakdown =
            Object.keys(visibleBreakdown).length > 0
              ? visibleBreakdown
              : automationStats.timeSavedBreakdown
              ? {
                  "Rent Reminders":
                    automationStats.timeSavedBreakdown.communication || 0,
                  "Maintenance Coordination":
                    automationStats.timeSavedBreakdown.maintenance || 0,
                  "Late Fee Processing":
                    automationStats.timeSavedBreakdown.financial || 0,
                  "Deposit Processing":
                    automationStats.timeSavedBreakdown.financial || 0,
                  Screening:
                    automationStats.timeSavedBreakdown.tenantManagement || 0,
                  Comms: automationStats.timeSavedBreakdown.communication || 0,
                  Other: automationStats.timeSavedBreakdown.other || 0,
                }
              : {};

          return (
            displayTimeSaved > 0 && (
              <TimeSavedSummary>
                <TimeSavedContent>
                  <TimeSavedLeft>
                    <TimeSavedHeader>
                      <TimeSavedIcon className="desktop-icon">
                        <HourglassOutlined />
                      </TimeSavedIcon>
                      <TimeSavedTitle>Time Saved This Week</TimeSavedTitle>
                    </TimeSavedHeader>
                    <TimeSavedValueContainer>
                      <TimeSavedIcon className="desktop-icon mobile-icon">
                        <HourglassOutlined />
                      </TimeSavedIcon>
                      <TimeSavedValue>
                        {formatTimeSaved(displayTimeSaved)}
                      </TimeSavedValue>
                    </TimeSavedValueContainer>
                  </TimeSavedLeft>

                  <TimeSavedRight>
                    {displayTimeSaved >= 60 && (
                      <TimeSavedSubtitle>
                        That's {Math.round(displayTimeSaved / 60)}{" "}
                        {pluralize("hour", Math.round(displayTimeSaved / 60))}{" "}
                        of your time back!
                      </TimeSavedSubtitle>
                    )}

                    {(() => {
                      const categories = Object.entries(
                        displayBreakdown
                      ).filter(([category, minutes]) => minutes > 0);
                      const categoryCount = categories.length;

                      if (categoryCount === 0) return null;

                      return (
                        <TimeSavedBreakdown
                          $isSingleCategory={categoryCount === 1}
                        >
                          {categories.map(([category, minutes]) => (
                            <TimeSavedCategory
                              key={category}
                              $isSingleCategory={categoryCount === 1}
                            >
                              <TimeSavedCategoryIcon>
                                {getCategoryIcon(category)}
                              </TimeSavedCategoryIcon>
                              <TimeSavedCategoryValue>
                                {formatTimeSaved(minutes)}
                              </TimeSavedCategoryValue>
                              <TimeSavedCategoryLabel>
                                {category}
                              </TimeSavedCategoryLabel>
                            </TimeSavedCategory>
                          ))}
                        </TimeSavedBreakdown>
                      );
                    })()}
                  </TimeSavedRight>
                </TimeSavedContent>
              </TimeSavedSummary>
            )
          );
        })()}

        {console.log(
          "[AI Dashboard] Rendering Timeline with recentActions:",
          recentActions
        )}
        {recentActions.length > 0 ? (
          <TimelineContainer>
            <Timeline
              items={(() => {
                // Separate actions into this week and last week
                const thisWeekActions = recentActions.filter(
                  (action) => !action.isLastWeek
                );
                const lastWeekActions = recentActions.filter(
                  (action) => action.isLastWeek
                );

                // Map this week's actions
                const thisWeekItems = thisWeekActions.map((action) => {
                  console.log(
                    "[AI Dashboard] Processing action for Timeline:",
                    action
                  );
                  const IconComponent = getIconComponent(action.icon);
                  return {
                    color: action.status === "completed" ? "green" : "green",
                    children: (
                      <TimelineItem>
                        <TimelineAvatar
                          icon={
                            <IconComponent
                              style={{ filter: "brightness(0) invert(1)" }}
                            />
                          }
                          style={{
                            backgroundColor:
                              action.status === "completed"
                                ? "var(--color-primary)"
                                : "#4A7C59",
                          }}
                        />
                        <TimelineContent>
                          {(action.assistQuestion || action.assistAnswer) &&
                            (action.actionDate || action.time) && (
                              <TimelineAssistDate>
                                <CalendarOutlined />
                                {action.actionDate || action.time}
                              </TimelineAssistDate>
                            )}
                          <TimelineAction
                            style={
                              action.assistQuestion || action.assistAnswer
                                ? { alignItems: "flex-start", flexWrap: "wrap" }
                                : undefined
                            }
                          >
                            {action.assistQuestion || action.assistAnswer ? (
                              renderLeoAssistTimelineBody(action)
                            ) : (
                              <>
                                <span className="desktop-action">
                                  {action.action}
                                </span>
                                <span className="mobile-action">
                                  {action.mobileAction}
                                </span>
                              </>
                            )}
                            {action.timeSaved > 0 && (
                              <TimelineTimeSaved className="desktop-time-saved">
                                <ClockCircleFilled />
                                {formatTimeSaved(action.timeSaved)} saved
                              </TimelineTimeSaved>
                            )}
                          </TimelineAction>
                          <TimelineDetails>
                            <TimelineDetailsRow>
                              <TimelineDetailsText className="desktop-details">
                                {action.tenant &&
                                action.tenant !== "Unknown Tenant" &&
                                !isUnknownProperty(action.unit)
                                  ? `${action.tenant} • ${action.unit}`
                                  : action.tenant &&
                                    action.tenant !== "Unknown Tenant"
                                  ? action.tenant
                                  : !isUnknownProperty(action.unit)
                                  ? action.unit
                                  : ""}
                              </TimelineDetailsText>
                              <TimelineDetailsText className="mobile-details">
                                {action.tenant &&
                                action.tenant !== "Unknown Tenant"
                                  ? action.tenant
                                  : ""}
                              </TimelineDetailsText>
                              {action.actionDate &&
                                !(
                                  action.assistQuestion || action.assistAnswer
                                ) && (
                                <TimelineDateLabel>
                                  <CalendarOutlined />
                                  {action.actionDate}
                                </TimelineDateLabel>
                                )}
                            </TimelineDetailsRow>
                            {action.timeSaved > 0 && (
                              <TimelineTimeSaved className="mobile-time-saved">
                                <ClockCircleFilled />
                                {formatTimeSaved(action.timeSaved)} saved
                              </TimelineTimeSaved>
                            )}
                          </TimelineDetails>
                        </TimelineContent>
                      </TimelineItem>
                    ),
                  };
                });

                // Add separator if there are last week actions
                const separatorItem =
                  lastWeekActions.length > 0
                    ? {
                        dot: null, // Remove the timeline dot
                        children: (
                          <TimelineSeparator className="timeline-separator">
                            Last Week
                          </TimelineSeparator>
                        ),
                      }
                    : null;

                // Map last week's actions
                const lastWeekItems = lastWeekActions.map((action) => {
                  const IconComponent = getIconComponent(action.icon);
                  return {
                    color: action.status === "completed" ? "green" : "green",
                    children: (
                      <TimelineItem>
                        <TimelineAvatar
                          icon={
                            <IconComponent
                              style={{ filter: "brightness(0) invert(1)" }}
                            />
                          }
                          style={{
                            backgroundColor:
                              action.status === "completed"
                                ? "var(--color-primary)"
                                : "#4A7C59",
                          }}
                        />
                        <TimelineContent>
                          {(action.assistQuestion || action.assistAnswer) &&
                            (action.actionDate || action.time) && (
                              <TimelineAssistDate>
                                <CalendarOutlined />
                                {action.actionDate || action.time}
                              </TimelineAssistDate>
                            )}
                          <TimelineAction
                            style={
                              action.assistQuestion || action.assistAnswer
                                ? { alignItems: "flex-start", flexWrap: "wrap" }
                                : undefined
                            }
                          >
                            {action.assistQuestion || action.assistAnswer ? (
                              renderLeoAssistTimelineBody(action)
                            ) : (
                              <>
                                <span className="desktop-action">
                                  {action.action}
                                </span>
                                <span className="mobile-action">
                                  {action.mobileAction}
                                </span>
                              </>
                            )}
                            {action.timeSaved > 0 && (
                              <TimelineTimeSaved className="desktop-time-saved">
                                <ClockCircleFilled />
                                {formatTimeSaved(action.timeSaved)} saved
                              </TimelineTimeSaved>
                            )}
                          </TimelineAction>
                          <TimelineDetails>
                            <TimelineDetailsRow>
                              <TimelineDetailsText className="desktop-details">
                                {action.tenant &&
                                action.tenant !== "Unknown Tenant" &&
                                !isUnknownProperty(action.unit)
                                  ? `${action.tenant} • ${action.unit}`
                                  : action.tenant &&
                                    action.tenant !== "Unknown Tenant"
                                  ? action.tenant
                                  : !isUnknownProperty(action.unit)
                                  ? action.unit
                                  : ""}
                              </TimelineDetailsText>
                              <TimelineDetailsText className="mobile-details">
                                {action.tenant &&
                                action.tenant !== "Unknown Tenant"
                                  ? action.tenant
                                  : ""}
                              </TimelineDetailsText>
                              {action.actionDate &&
                                !(
                                  action.assistQuestion || action.assistAnswer
                                ) && (
                                <TimelineDateLabel>
                                  <CalendarOutlined />
                                  {action.actionDate}
                                </TimelineDateLabel>
                                )}
                            </TimelineDetailsRow>
                            {action.timeSaved > 0 && (
                              <TimelineTimeSaved className="mobile-time-saved">
                                <ClockCircleFilled />
                                {formatTimeSaved(action.timeSaved)} saved
                              </TimelineTimeSaved>
                            )}
                          </TimelineDetails>
                        </TimelineContent>
                      </TimelineItem>
                    ),
                  };
                });

                // Combine all items: this week, separator (if needed), last week
                return separatorItem
                  ? [...thisWeekItems, separatorItem, ...lastWeekItems]
                  : [...thisWeekItems, ...lastWeekItems];
              })()}
            />
          </TimelineContainer>
        ) : (
          <TodayEmptyState>
            <TodayEmptyIcon>
              <ClockCircleOutlined />
            </TodayEmptyIcon>
            <TodayEmptyTitle>No recent activities</TodayEmptyTitle>
            <TodayEmptyDescription>
              Leo hasn't performed any automation tasks yet. Make sure Leo has
              the necessary permissions to help manage your properties.
            </TodayEmptyDescription>
            <TodayEmptyAction>
              <TodayEmptyButton
                type="primary"
                size="small"
                onClick={() => navigate("/settings")}
              >
                <SettingOutlined />
                Check Leo Permissions
              </TodayEmptyButton>
            </TodayEmptyAction>
          </TodayEmptyState>
        )}
      </SectionCard>

      {showLeoGuide && (
        <GuideOverlay onClick={() => setShowLeoGuide(false)}>
          <GuideModal
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="About Leo assistant"
          >
            <GuideHeader>
              <GuideHeaderLeft>
                <GuideHeaderIcon>
                  <img
                    src="/icons/leo/leo.png"
                    alt="Leo AI"
                    style={{
                      width: "32px",
                      height: "32px",
                      filter: "brightness(0) invert(1)",
                    }}
                  />
                </GuideHeaderIcon>
                <GuideTitle>
                  {isMobile
                    ? "Your AI Property Assistant"
                    : "Meet Leo - Your AI Property Assistant"}
                </GuideTitle>
              </GuideHeaderLeft>
              <GuideClose onClick={() => setShowLeoGuide(false)}>✕</GuideClose>
            </GuideHeader>
            <GuideBody>
              <GuideHero>
                <HeroContent>
                  <HeroTitle>
                    Automate the busywork,{isMobile ? <br /> : " "}stay in
                    control
                  </HeroTitle>
                  <HeroSubtitle>
                    Leo responds to tenants, coordinates vendors, and keeps rent
                    moving while you focus on owners and growth.
                  </HeroSubtitle>
                  <HeroHighlights>
                    <HeroHighlight>
                      <HighlightIcon $bg="#2C5F2D">
                        <ClockCircleFilled
                          style={{ filter: "brightness(0) invert(1)" }}
                        />
                      </HighlightIcon>
                      <HighlightText>
                        <HighlightTitle>Always on duty</HighlightTitle>
                        <HighlightCopy>
                          Instant replies and triage even after hours.
                        </HighlightCopy>
                      </HighlightText>
                    </HeroHighlight>
                    <HeroHighlight>
                      <HighlightIcon $bg="#16a34a">
                        <CheckCircleFilled
                          style={{ filter: "brightness(0) invert(1)" }}
                        />
                      </HighlightIcon>
                      <HighlightText>
                        <HighlightTitle>Actionable insights</HighlightTitle>
                        <HighlightCopy>
                          Leo identifies overdue payments, maintenance issues,
                          and recommends next actions.
                        </HighlightCopy>
                      </HighlightText>
                    </HeroHighlight>
                    <HeroHighlight>
                      <HighlightIcon $bg="#f59e0b">
                        <MessageOutlined
                          style={{ filter: "brightness(0) invert(1)" }}
                        />
                      </HighlightIcon>
                      <HighlightText>
                        <HighlightTitle>Seamless handoffs</HighlightTitle>
                        <HighlightCopy>
                          Conversations escalate with context when you need to
                          step in.
                        </HighlightCopy>
                      </HighlightText>
                    </HeroHighlight>
                  </HeroHighlights>
                </HeroContent>
              </GuideHero>

              <GuideSection>
                <GuideSectionTitle>
                  <ThunderboltOutlined /> What Leo can do
                </GuideSectionTitle>
                <GuideList>
                  <li>
                    <BulletIcon $bg="#2c5f2d" $color="#ffffff">
                      <CheckCircleFilled />
                    </BulletIcon>
                    <span>
                      Send rent reminders, thank-you receipts, and follow-ups
                      automatically.
                    </span>
                  </li>
                  <li>
                    <BulletIcon $bg="#2c5f2d" $color="#ffffff">
                      <CheckCircleFilled />
                    </BulletIcon>
                    <span>
                      Triage maintenance, request photos, and coordinate vendors
                      with clear timelines.
                    </span>
                  </li>
                  <li>
                    <BulletIcon $bg="#2c5f2d" $color="#ffffff">
                      <CheckCircleFilled />
                    </BulletIcon>
                    <span>
                      Answer tenant FAQs and escalate only when a human decision
                      is needed.
                    </span>
                  </li>
                </GuideList>
              </GuideSection>
              <GuideSection>
                <GuideSectionTitle>
                  <img
                    src="/icons/leo/leo.png"
                    alt="Leo AI"
                    style={{
                      width: "20px",
                      height: "20px",
                      marginRight: "8px",
                    }}
                  />{" "}
                  Tips for best results
                </GuideSectionTitle>
                <GuideList>
                  <li>
                    <BulletIcon $bg="#b45309" $color="#ffffff">
                      <img
                        src="/icons/leo/leo.png"
                        alt="Leo AI"
                        style={{ width: "16px", height: "16px" }}
                      />
                    </BulletIcon>
                    <span>
                      Be direct: ask Leo to send a gentle rent reminder to Unit
                      203 for tomorrow.
                    </span>
                  </li>
                  <li>
                    <BulletIcon $bg="#b45309" $color="#ffffff">
                      <img
                        src="/icons/leo/leo.png"
                        alt="Leo AI"
                        style={{ width: "16px", height: "16px" }}
                      />
                    </BulletIcon>
                    <span>
                      Use plain language. Leo understands intent and fills in
                      details.
                    </span>
                  </li>
                  <li>
                    <BulletIcon $bg="#b45309" $color="#ffffff">
                      <img
                        src="/icons/leo/leo.png"
                        alt="Leo AI"
                        style={{ width: "16px", height: "16px" }}
                      />
                    </BulletIcon>
                    <span>
                      Review weekly summaries to fine-tune automations and
                      rules.
                    </span>
                  </li>
                </GuideList>
              </GuideSection>
              <GuideActions>
                <GuideSecondary onClick={() => setShowLeoGuide(false)}>
                  Close
                </GuideSecondary>
              </GuideActions>
            </GuideBody>
          </GuideModal>
        </GuideOverlay>
      )}

      {/* Quick Actions */}
      {/*
      <SectionCard title="Quick Actions" className="section-card">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <QuickActionCard onClick={() => handleQuickAction('Send bulk reminders')}>
              <QuickActionIcon>
                <MessageOutlined />
              </QuickActionIcon>
              <QuickActionText>Send Bulk Reminders</QuickActionText>
            </QuickActionCard>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <QuickActionCard onClick={() => handleQuickAction('Generate monthly report')}>
              <QuickActionIcon>
                <FileTextOutlined />
              </QuickActionIcon>
              <QuickActionText>Generate Monthly Report</QuickActionText>
            </QuickActionCard>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <QuickActionCard onClick={() => handleQuickAction('Screen new applicants')}>
              <QuickActionIcon>
                <SafetyOutlined />
              </QuickActionIcon>
              <QuickActionText>Screen New Applicants</QuickActionText>
            </QuickActionCard>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <QuickActionCard onClick={() => handleQuickAction('Process deposits')}>
              <QuickActionIcon>
                <BankOutlined />
              </QuickActionIcon>
              <QuickActionText>Process Deposits</QuickActionText>
            </QuickActionCard>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <QuickActionCard onClick={() => handleQuickAction('Schedule inspections')}>
              <QuickActionIcon>
                <CalendarOutlined />
              </QuickActionIcon>
              <QuickActionText>Schedule Inspections</QuickActionText>
            </QuickActionCard>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <QuickActionCard onClick={() => handleQuickAction('Legal compliance check')}>
              <QuickActionIcon>
                <SafetyOutlined />
              </QuickActionIcon>
              <QuickActionText>Legal Compliance Check</QuickActionText>
            </QuickActionCard>
          </Col>
        </Row>
      </SectionCard>
      */}
    </Container>
  );
};

// Enhanced Styled Components
const Container = styled.div`
  min-height: 100vh;
  background: var(--color-bg-primary);
  padding: 2rem 2rem;
  width: ${LAYOUT_CONSTANTS.DESKTOP_CONTENT_WIDTH};
  margin: 0 auto;

  @media ${device.mobile} {
    padding: 1rem;
    width: 95vw;
    min-width: ${LAYOUT_CONSTANTS.MIN_CONTENT_WIDTH};
  }

  @media (min-width: 768px) and (max-width: 1200px) {
    width: 90vw;
  }

  &.ai-automation-dashboard {
    background: var(--color-bg-primary) !important;
  }
`;

const AuthorizationSection = styled.div`
  margin-top: 2rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 1.5rem;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(8px);

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: rgba(255, 255, 255, 0.3);
  }
`;

const AuthorizationCard = styled(Card)`
  border-radius: 12px;
  border: none;
  box-shadow: none;
  background: transparent;
  padding: 0;

  .ant-card-head {
    background: var(--color-primary) !important;
    border-bottom: none;
    padding: 0;
  }

  .ant-card-head-title {
    padding: 0;
    font-family: var(--font-primary);
    font-size: 1.25rem;
    font-weight: 700;
    color: white !important;
  }

  .ant-card-body {
    padding: 0;
  }
`;

const AuthorizationHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
  position: relative;
`;

const AuthorizationIcon = styled.div`
  font-size: 1.5rem;
  color: rgba(255, 255, 255, 0.9);
  flex-shrink: 0;
`;

const AuthorizationTitle = styled.div`
  font-family: var(--font-primary);
  font-size: 1.25rem;
  font-weight: 700;
  color: white;
  flex: 1;
`;

const AuthorizationDescription = styled.p`
  font-family: var(--font-secondary);
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.5;
  margin-bottom: 1.5rem;
  max-width: 600px;
`;

const AuthorizationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;
`;

const AuthorizationItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  background: ${(props) =>
    props.authorized ? "var(--color-primary)" : "#f8fafc"};
  border-radius: 12px;
  border: 2px solid
    ${(props) => (props.authorized ? "var(--color-primary)" : "#e2e8f0")};
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px
      ${(props) =>
        props.authorized ? "rgba(44, 95, 45, 0.2)" : "rgba(44, 95, 45, 0.1)"};
  }

  ${(props) =>
    props.authorized
      ? `
    color: white;
    
    ${AuthItemIcon} {
      color: white;
    }
    
    ${AuthItemText} {
      color: white;
      font-weight: 600;
    }
    
    ${AuthItemStatus} {
      background: rgba(255, 255, 255, 0.2);
      border: 1px solid rgba(255, 255, 255, 0.3);
      color: white;
    }
  `
      : `
    color: #64748b;
    
    ${AuthItemIcon} {
      color: var(--color-primary);
    }
    
    ${AuthItemText} {
      color: #1e293b;
      font-weight: 500;
    }
    
    ${AuthItemStatus} {
      background: #fffbe6;
      border: 1px solid #faad14;
      color: #d97706;
    }
  `}
`;

const AuthItemIcon = styled.div`
  font-size: 1.5rem;
  color: var(--color-primary);
  margin-right: 0.75rem;
  flex-shrink: 0;
`;

const AuthItemText = styled.div`
  font-family: var(--font-secondary);
  font-weight: 500;
  font-size: 0.9rem;
  color: #1e293b;
  flex: 1;
  line-height: 1.4;
`;

const AuthItemStatus = styled.div`
  font-family: var(--font-secondary);
  font-size: 0.75rem;
  font-weight: 600;
  color: ${(props) => (props.authorized ? "white" : "#d97706")};
  padding: 4px 8px;
  border-radius: 12px;
  border: 1px solid
    ${(props) => (props.authorized ? "rgba(255, 255, 255, 0.3)" : "#faad14")};
  background: ${(props) =>
    props.authorized ? "rgba(255, 255, 255, 0.2)" : "#fffbe6"};
  text-transform: uppercase;
  letter-spacing: 0.3px;
  min-width: 80px;
  text-align: center;
`;

const AuthorizationFooter = styled.div`
  text-align: center;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
`;

const SettingsLink = styled(RouterLink)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--gradient-primary);
  border: none;
  border-radius: 8px;
  font-family: var(--font-secondary);
  font-weight: 600;
  font-size: 0.9rem;
  color: white;
  padding: 0.75rem 1.5rem;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(44, 95, 45, 0.2);
  text-decoration: none;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(44, 95, 45, 0.25);
    color: white;
    text-decoration: none;
  }
`;

const SettingsIconLink = styled(RouterLink)`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  font-size: 1.5rem;
  color: rgba(255, 255, 255, 0.9);
  transition: color 0.2s ease;
  z-index: 2;

  &:hover {
    color: white;
  }
`;

const SectionCard = styled(Card)`
  border-radius: 20px;
  border: 2px solid var(--color-border-light);
  box-shadow: 0 8px 32px rgba(44, 95, 45, 0.08);
  background: white;
  margin-bottom: 2rem;
  overflow: hidden;

  .ant-card-head {
    background: var(--color-primary) !important;
    border-bottom: 2px solid var(--color-border-light);
    padding: 0;
  }

  .ant-card-head-title {
    padding: 1.5rem 2rem 1rem 2rem;
    font-family: var(--font-primary);
    font-size: 1.5rem;
    font-weight: 700;
    color: white !important;
  }

  .ant-card-body {
    padding: 2rem;
  }

  &.section-card {
    background: #ffffff !important;
    border: 2px solid #e2e8f0 !important;
    border-radius: 20px !important;
    box-shadow: 0 8px 32px rgba(44, 95, 45, 0.08) !important;
    margin-bottom: 2rem !important;
    overflow: hidden !important;
  }
`;

const StatsSection = styled.div`
  margin-bottom: 2rem;
  width: 100%;

  .ant-card {
    --stat-title-size: 0.875rem;
    --stat-value-size: 1.5rem;
    --stat-suffix-size: 1.5rem;
    --stat-icon-size: 1.5rem;
    height: 100%;
  }

  .ant-statistic {
    text-align: center;
  }

  .ant-statistic-title {
    font-family: var(--font-secondary) !important;
    font-size: var(--stat-title-size) !important;
    line-height: 1.25;
    font-weight: 600 !important;
    color: var(--color-text-light);
    margin-bottom: 0.5rem;
  }

  .ant-statistic-content {
    font-family: var(--font-primary) !important;
    font-size: var(--stat-value-size) !important;
    line-height: 1.1;
    font-weight: 700 !important;
    color: var(--color-text) !important;
  }

  .ant-statistic-content-value,
  .ant-statistic-content-value-int,
  .ant-statistic-content-value-decimal {
    font-size: var(--stat-value-size) !important;
    line-height: 1.1;
    font-weight: 700 !important;
    color: var(--color-text) !important;
  }

  .ant-statistic-content-suffix,
  .ant-statistic-content-prefix {
    font-size: var(--stat-suffix-size) !important;
    font-weight: 700 !important;
    line-height: 1.2;
  }

  .ant-statistic .ant-statistic-content-prefix svg,
  .ant-statistic .ant-statistic-content-prefix .anticon {
    font-size: var(--stat-icon-size) !important;
    width: var(--stat-icon-size) !important;
    height: var(--stat-icon-size) !important;
  }

  .stats-card {
    .ant-statistic-title {
      font-size: var(--stat-title-size) !important;
      font-weight: 600 !important;
    }
    .ant-statistic-content {
      font-size: var(--stat-value-size) !important;
      font-weight: 700 !important;
    }
    .ant-statistic-content-value,
    .ant-statistic-content-value-int,
    .ant-statistic-content-value-decimal {
      font-size: var(--stat-value-size) !important;
      font-weight: 700 !important;
    }
    .ant-statistic-content-suffix,
    .ant-statistic-content-prefix {
      font-size: var(--stat-suffix-size) !important;
      font-weight: 700 !important;
    }
    .ant-statistic-content-prefix svg,
    .ant-statistic-content-prefix .anticon {
      font-size: var(--stat-icon-size) !important;
      width: var(--stat-icon-size) !important;
      height: var(--stat-icon-size) !important;
    }
  }

  @media ${device.mobile} {
    .ant-card {
      --stat-title-size: 0.8rem;
      --stat-value-size: 1.25rem;
      --stat-suffix-size: 1.25rem;
      --stat-icon-size: 1.25rem;
    }
  }
`;

// Time Saved Summary Components
const TimeSavedSummary = styled.div`
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 2px solid var(--color-primary);
  border-radius: 16px;
  padding: 1.5rem 2rem;
  margin-bottom: 1.5rem;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(44, 95, 45, 0.08);

  @media (max-width: 768px) {
    padding: 1.25rem 1.5rem;
    margin-bottom: 1.25rem;
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(
      90deg,
      var(--color-primary) 0%,
      var(--color-primary-dark) 50%,
      var(--color-primary-light) 100%
    );
  }
`;

const TimeSavedContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;

  @media ${device.mobile} {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
`;

const TimeSavedLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 0.5rem;
  }
`;

const TimeSavedHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.25rem;
  }
`;

const TimeSavedIcon = styled.div`
  font-size: 1.5rem;
  color: var(--color-primary);
  filter: drop-shadow(0 2px 4px rgba(44, 95, 45, 0.2));

  @media (max-width: 768px) {
    font-size: 1.25rem;

    &.desktop-icon {
      display: none;
    }

    &.mobile-icon {
      display: block;
    }
  }

  @media (min-width: 769px) {
    &.desktop-icon {
      display: block;
    }

    &.mobile-icon {
      display: none;
    }
  }
`;

const TimeSavedTitle = styled.div`
  font-family: var(--font-primary);
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--color-text);
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 0.95rem;
    text-align: center;
  }
`;

const TimeSavedValue = styled.div`
  font-family: var(--font-primary);
  font-size: 2.5rem;
  font-weight: 900;
  color: var(--color-primary);
  line-height: 1;
  text-shadow: 0 1px 2px rgba(44, 95, 45, 0.1);

  @media (max-width: 768px) {
    font-size: 2rem;
    margin-top: 0.5rem;
  }
`;

const TimeSavedValueContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: 768px) {
    justify-content: center;
    gap: 0.5rem;
  }
`;

const TimeSavedSubtitle = styled.div`
  font-family: var(--font-secondary);
  font-size: 0.9rem;
  color: var(--color-text-muted);
  font-weight: 500;
  line-height: 1.4;
  margin-top: 0.25rem;

  @media (max-width: 768px) {
    font-size: 0.875rem;
    text-align: center;
    margin-top: 0.5rem;
    margin-bottom: 1rem;
    line-height: 1.4;
  }
`;

const TimeSavedRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.75rem;
  flex: 0 0 auto;
`;

const TimeSavedBreakdown = styled.div`
  display: flex;
  flex-wrap: nowrap;
  justify-content: ${(props) =>
    props.$isSingleCategory ? "center" : "space-between"};
  gap: 0.75rem;
  width: 100%;

  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: ${(props) =>
      props.$isSingleCategory ? "1fr" : "repeat(2, 1fr)"};
    gap: 0.75rem;
    max-width: 100%;
  }
`;

const TimeSavedCategory = styled.div`
  text-align: center;
  padding: 0.75rem 0.5rem;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.9) 0%,
    rgba(248, 250, 252, 0.9) 100%
  );
  border-radius: 10px;
  border: 1px solid rgba(44, 95, 45, 0.15);
  box-shadow: 0 1px 4px rgba(44, 95, 45, 0.08);
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: ${(props) => (props.$isSingleCategory ? "0 0 auto" : "1")};
  min-width: ${(props) => (props.$isSingleCategory ? "200px" : "0")};
  max-width: ${(props) => (props.$isSingleCategory ? "300px" : "none")};

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(44, 95, 45, 0.12);
    border-color: rgba(44, 95, 45, 0.25);
  }

  @media (max-width: 768px) {
    min-width: 0;
    max-width: 100%;
  }
`;

const TimeSavedCategoryIcon = styled.div`
  font-size: 1rem;
  color: var(--color-primary);
  margin-bottom: 0.25rem;
`;

const TimeSavedCategoryValue = styled.div`
  font-family: var(--font-primary);
  font-size: 1rem;
  font-weight: 800;
  color: var(--color-primary);
  margin-bottom: 0.125rem;
  text-shadow: 0 1px 2px rgba(44, 95, 45, 0.1);
`;

const TimeSavedCategoryLabel = styled.div`
  font-family: var(--font-secondary);
  font-size: 0.7rem;
  color: var(--color-text-muted);
  font-weight: 600;
  text-transform: capitalize;
  line-height: 1.2;
`;

const TimeSavedBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  background: linear-gradient(
    135deg,
    var(--color-primary) 0%,
    var(--color-primary-dark) 100%
  );
  color: white !important;
  padding: 4px 8px;
  border-radius: 12px;
  font-family: var(--font-secondary);
  font-size: 0.75rem;
  font-weight: 600;
  margin-left: 0.5rem;

  .anticon {
    color: white !important;
    filter: brightness(0) invert(1) !important;
  }

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    font-size: 0.75rem;
    padding: 4px 8px;
    margin-left: 0;
    margin-top: 0.25rem;
    display: inline-flex;
  }
`;

const QuickActionCard = styled.div`
  background: white;
  border: 2px solid var(--color-border-light);
  border-radius: 16px;
  padding: 2rem 1.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  height: 100%;

  &:hover {
    border-color: var(--color-primary);
    background-color: rgba(44, 95, 45, 0.02);
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(44, 95, 45, 0.1);
  }
`;

const QuickActionIcon = styled.div`
  font-size: 2.5rem;
  color: var(--color-primary);
  margin-bottom: 1rem;
  filter: drop-shadow(0 2px 8px rgba(44, 95, 45, 0.2));
`;

const QuickActionText = styled.div`
  font-family: var(--font-secondary);
  font-size: 1rem;
  color: #1e293b;
  font-weight: 600;
  line-height: 1.4;
`;

const TimelineContainer = styled.div`
  /* Make timeline dots match avatar background colors - keep white center */
  .ant-timeline-item-head {
    border-color: var(--color-primary) !important;
    background-color: #ffffff !important;
  }

  .ant-timeline-item-head-blue {
    border-color: #4A7C59 !important;
    background-color: #ffffff !important;
  }

  .ant-timeline-item-head-green {
    border-color: var(--color-primary) !important;
    background-color: #ffffff !important;
  }

  /* Hide timeline dot for separator items */
  .ant-timeline-item:has(.timeline-separator) .ant-timeline-item-head {
    display: none !important;
  }

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
    scrollbar-color: var(--color-primary) transparent;

    /* Custom scrollbar styling for webkit browsers */
    &::-webkit-scrollbar {
      height: 8px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: var(--color-primary);
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: var(--color-primary-dark);
    }
  }
`;

const TimelineItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem 0;

  /* Desktop layout improvements */
  @media (min-width: 769px) {
    gap: 1rem;
    padding: 1rem 0;
    align-items: flex-start;
  }

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    gap: 0.5rem;
    padding: 0.75rem 0;
    border-bottom: 1px solid #f0f0f0;
    margin-bottom: 0.5rem;

    &:last-child {
      border-bottom: none;
      margin-bottom: 0;
    }
  }
`;

const TimelineSeparator = styled.div`
  font-family: var(--font-primary);
  font-weight: 600;
  font-size: 0.875rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 1rem 0 0.5rem 0;
  margin-top: 1rem;
  border-top: 1px solid #e2e8f0;
  padding-top: 1.5rem;
  margin-left: 0; /* Remove left margin to align with content */

  @media (max-width: 768px) {
    font-size: 0.8rem;
    padding: 1rem 0 0.5rem 0;
    margin-top: 0.5rem;
    padding-top: 1rem;
  }
`;

const TimelineAvatar = styled(Avatar)`
  flex-shrink: 0;

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    width: 28px !important;
    height: 28px !important;
    font-size: 12px !important;
  }
`;

const TimelineContent = styled.div`
  flex: 1;

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    width: 100%;
    order: 2;
  }
`;

const TimelineAction = styled.div`
  font-family: var(--font-primary);
  font-weight: 600;
  font-size: 1rem;
  color: #1e293b;
  margin-bottom: 0.25rem;
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 0.5rem;

  .desktop-action {
    flex: 1;
    min-width: 0;
    text-align: left;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }

  .mobile-action {
    display: none;
    text-align: left;
  }

  /* Desktop layout improvements */
  @media (min-width: 769px) {
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
    flex-wrap: nowrap;
  }

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    font-size: 0.85rem;
    line-height: 1.3;
    margin-bottom: 0.25rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
    text-align: left;
    flex-wrap: wrap;

    .desktop-action {
      display: none;
      text-align: left;
    }

    .mobile-action {
      display: block;
      text-align: left;
      width: 100%;
    }
  }
`;

const TimelineAssistWrap = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  text-align: left;
  font-weight: 400;
  padding: 0.15rem 0;
`;

const TimelineAssistDate = styled.div`
  display: flex;
  width: fit-content;
  min-height: 32px;
  align-items: center;
  gap: 0.3rem;
  margin: 0 0 0.5rem;
  text-align: left;
  color: #64748b;
  font-family: var(--font-secondary);
  font-size: 0.74rem;
  font-weight: 500;

  .anticon {
    font-size: 0.7rem;
    opacity: 0.75;
  }

  @media (max-width: 768px) {
    min-height: 28px;
  }
`;

const TimelineAssistBlock = styled.div`
  display: block;
  align-self: ${(props) =>
    props.$role === "user" ? "flex-end" : "flex-start"};
  width: fit-content;
  max-width: min(82%, 760px);
  padding: 0.75rem 0.95rem;
  border-radius: ${(props) =>
    props.$role === "user"
      ? "16px 16px 4px 16px"
      : "16px 16px 16px 4px"};
  background: ${(props) =>
    props.$role === "user"
      ? "linear-gradient(135deg, var(--color-primary, #2c5f2d), #4a7c59)"
      : "#f3f7f3"};
  border: 1px solid
    ${(props) =>
      props.$role === "user" ? "rgba(44, 95, 45, 0.3)" : "#dbe7dc"};
  box-shadow: ${(props) =>
    props.$role === "user"
      ? "0 4px 12px rgba(44, 95, 45, 0.18)"
      : "0 2px 8px rgba(15, 23, 42, 0.07)"};
  line-height: 1.5;

  @media (max-width: 768px) {
    max-width: 92%;
    padding: 0.7rem 0.85rem;
  }
`;

const TimelineAssistLabel = styled.span`
  display: block;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${(props) =>
    props.$role === "user"
      ? "rgba(255, 255, 255, 0.82) !important"
      : "#527057"};
  margin-bottom: 0.25rem;
  text-align: ${(props) => (props.$role === "user" ? "right" : "left")};
`;

const TimelineAssistText = styled.div`
  font-size: 0.92rem;
  line-height: 1.55;
  font-weight: 450;
  color: ${(props) =>
    props.$role === "user" ? "#ffffff !important" : "#1e293b"};
  font-family: var(--font-secondary);
  white-space: pre-wrap;
  word-break: break-word;

  strong {
    font-weight: 700;
    color: inherit;
  }
`;

const LeoAssistExpandBtn = styled.button`
  margin-top: 0.55rem;
  padding: 0;
  border: none;
  background: none;
  font-family: var(--font-primary);
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--color-primary, #2c5f2d);
  cursor: pointer;
  text-align: left;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    opacity: 0.85;
  }

  &:focus-visible {
    outline: 2px solid var(--color-primary, #2c5f2d);
    outline-offset: 2px;
  }
`;

const TimelineDetails = styled.div`
  font-family: var(--font-secondary);
  font-size: 0.9rem;
  color: #64748b;
  font-weight: 500;

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    font-size: 0.8rem;
    line-height: 1.3;
    word-wrap: break-word;
    overflow-wrap: break-word;
    margin-bottom: 0.5rem;
  }
`;

const TimelineDetailsRow = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 0.25rem;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.125rem;
    margin-bottom: 0.25rem;

    span {
      text-align: left !important;
    }
  }
`;

const TimelineDetailsText = styled.span`
  font-size: inherit;
  color: inherit;

  &.desktop-details {
    @media (min-width: 769px) {
      display: block;
    }

    @media (max-width: 768px) {
      display: none;
    }
  }

  &.mobile-details {
    @media (min-width: 769px) {
      display: none;
    }

    @media (max-width: 768px) {
      display: block;
    }
  }

  @media (max-width: 768px) {
    font-size: 0.75rem;
    color: #9ca3af;
    text-align: center;
    width: 100%;
  }
`;

const TimelineDateLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.72rem;
  color: #6b7280;
  white-space: nowrap;
  flex-shrink: 0;

  .anticon {
    font-size: 0.68rem;
    opacity: 0.7;
  }

  @media (max-width: 768px) {
    font-size: 0.7rem;
  }
`;

const TimelineTimeSaved = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  background: linear-gradient(
    135deg,
    var(--color-primary) 0%,
    var(--color-primary-dark) 100%
  );
  color: white !important;
  padding: 2px 6px;
  border-radius: 8px;
  font-family: var(--font-secondary);
  font-size: 0.7rem;
  font-weight: 600;
  margin-top: 0.25rem;
  flex-shrink: 0;
  white-space: nowrap;

  .anticon {
    color: white !important;
    filter: brightness(0) invert(1) !important;
    font-size: 0.7rem;
  }

  /* Desktop positioning */
  &.desktop-time-saved {
    @media (min-width: 769px) {
      display: inline-flex;
      font-size: 0.75rem;
      padding: 3px 8px;
      margin-left: 0.5rem;
      margin-top: 0;
      flex-shrink: 0;
      white-space: nowrap;
    }

    @media (max-width: 768px) {
      display: none;
    }
  }

  /* Mobile positioning */
  &.mobile-time-saved {
    @media (min-width: 769px) {
      display: none;
    }

    @media (max-width: 768px) {
      display: inline-flex;
      font-size: 0.65rem;
      padding: 2px 5px;
      margin-top: 0.25rem;
      align-self: center;
    }
  }
`;

const TimelineTag = styled(Tag)`
  flex-shrink: 0;
  font-weight: 600;
  font-size: 0.85rem;
  padding: 4px 12px;
  border-radius: 20px;

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    order: 3;
    align-self: flex-start;
    font-size: 0.8rem;
    padding: 4px 12px;
    margin-top: 0.25rem;
  }
`;

const ActionButton = styled(Button)`
  background: var(--gradient-primary) !important;
  border: none !important;
  border-radius: 12px !important;
  font-family: var(--font-primary) !important;
  font-weight: 600 !important;
  font-size: 0.9rem !important;
  height: 36px !important;
  padding: 0 16px !important;
  box-shadow: 0 4px 16px rgba(44, 95, 45, 0.2) !important;

  &:hover {
    transform: translateY(-2px) !important;
    box-shadow: 0 8px 24px rgba(44, 95, 45, 0.3) !important;
  }
`;

const ActionAvatar = styled(Avatar)`
  flex-shrink: 0;
`;

const ActionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-family: var(--font-primary);
  font-weight: 600;
  font-size: 1.1rem;
  color: #1e293b;
`;

const PriorityTag = styled(Tag)`
  font-weight: 600;
  font-size: 0.8rem;
  padding: 2px 8px;
  border-radius: 16px;
`;

const ActionDescription = styled.div`
  font-family: var(--font-secondary);
  font-size: 0.95rem;
  color: #475569;
  line-height: 1.5;
`;

const ActionDetails = styled.div`
  font-family: var(--font-secondary);
  font-size: 0.85rem;
  color: #64748b;
  margin-top: 0.5rem;
  font-weight: 500;
`;

const InsightButton = styled(Button)`
  color: var(--color-primary) !important;
  border-color: var(--color-primary) !important;
  border-radius: 12px !important;
  font-family: var(--font-secondary) !important;
  font-weight: 600 !important;
  font-size: 0.9rem !important;
  height: 36px !important;
  padding: 0 16px !important;

  &:hover {
    background: var(--color-primary) !important;
    color: white !important;
    transform: translateY(-2px) !important;
    box-shadow: 0 8px 24px rgba(44, 95, 45, 0.2) !important;
  }
`;

const InsightAvatar = styled(Avatar)`
  flex-shrink: 0;
`;

const InsightTitle = styled.div`
  font-family: var(--font-primary);
  font-weight: 600;
  font-size: 1.1rem;
  color: #1e293b;
  margin-bottom: 0.5rem;
`;

const InsightDescription = styled.div`
  font-family: var(--font-secondary);
  font-size: 0.95rem;
  color: #475569;
  line-height: 1.5;
`;

const TrustAlert = styled(Alert)`
  margin-bottom: 2rem;
  border-radius: 12px;
  border: 2px solid #e2e8f0;

  .ant-alert-message {
    font-family: var(--font-primary);
    font-weight: 600;
    font-size: 1.1rem;
    color: #1e293b;
  }

  .ant-alert-description {
    font-family: var(--font-secondary);
    font-size: 1rem;
    color: #475569;
    line-height: 1.6;
  }
`;

const TrustGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const TrustItem = styled.div`
  text-align: center;
  padding: 2rem 1.5rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 16px;
  border: 2px solid #e2e8f0;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(44, 95, 45, 0.1);
    border-color: var(--color-primary);
  }
`;

const TrustIcon = styled.div`
  font-size: 2.5rem;
  color: var(--color-primary);
  margin-bottom: 1rem;
  filter: drop-shadow(0 2px 8px rgba(44, 95, 45, 0.2));
`;

const TrustTitle = styled.div`
  font-family: var(--font-primary);
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.5rem;
`;

const TrustDescription = styled.div`
  font-family: var(--font-secondary);
  font-size: 0.95rem;
  color: #64748b;
  line-height: 1.5;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px; /* Adjust as needed */
  padding: 2rem;
  background: white;
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  margin-top: 2rem;
`;

const LoadingSpinner = styled(Spin)`
  font-size: 3rem;
  color: var(--color-primary);
  margin-bottom: 1rem;
`;

const LoadingText = styled.p`
  font-family: var(--font-secondary);
  font-size: 1.1rem;
  color: #64748b;
  font-weight: 500;
`;

// Empty State Styled Components
const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 3rem 2rem;
  text-align: center;
  background: white;
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  margin-top: 2rem;
`;

const EmptyStateIcon = styled.div`
  font-size: 4rem;
  color: var(--color-primary);
  margin-bottom: 1.5rem;
  filter: drop-shadow(0 4px 12px rgba(44, 95, 45, 0.2));
`;

const EmptyStateTitle = styled.h2`
  font-family: var(--font-primary);
  font-size: 2rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1rem;
`;

const EmptyStateDescription = styled.p`
  font-family: var(--font-secondary);
  font-size: 1.1rem;
  color: #64748b;
  line-height: 1.6;
  max-width: 600px;
  margin-bottom: 2.5rem;
`;

const EmptyStateActions = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 3rem;
  flex-wrap: wrap;
  justify-content: center;

  @media ${device.mobile} {
    flex-direction: column;
    align-items: center;
    width: 100%;
  }
`;

const EmptyStateButton = styled(Button)`
  background: var(--gradient-primary) !important;
  border: none !important;
  border-radius: 12px !important;
  font-family: var(--font-primary) !important;
  font-weight: 600 !important;
  font-size: 1rem !important;
  height: 48px !important;
  padding: 0 2rem !important;
  box-shadow: 0 4px 16px rgba(44, 95, 45, 0.2) !important;
  display: flex !important;
  align-items: center !important;
  gap: 0.5rem !important;

  span {
    filter: brightness(0) invert(1);
  }

  .anticon {
    filter: brightness(0) invert(1);
  }

  &:hover {
    transform: translateY(-2px) !important;
    box-shadow: 0 8px 24px rgba(44, 95, 45, 0.3) !important;
  }

  @media ${device.mobile} {
    width: 100%;
    max-width: 300px;
  }
`;

const EmptyStateSecondaryButton = styled(Button)`
  color: var(--color-primary) !important;
  border-color: var(--color-primary) !important;
  border-radius: 12px !important;
  font-family: var(--font-secondary) !important;
  font-weight: 600 !important;
  font-size: 1rem !important;
  height: 48px !important;
  padding: 0 2rem !important;
  display: flex !important;
  align-items: center !important;
  gap: 0.5rem !important;

  &:hover {
    background: var(--color-primary) !important;
    color: white !important;
    transform: translateY(-2px) !important;
    box-shadow: 0 8px 24px rgba(44, 95, 45, 0.2) !important;
  }

  @media ${device.mobile} {
    width: 100%;
    max-width: 300px;
  }
`;

const EmptyStateFeatures = styled.div`
  max-width: 800px;
  width: 100%;
`;

const EmptyStateFeatureTitle = styled.h3`
  font-family: var(--font-primary);
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 1.5rem;
`;

const EmptyStateFeatureList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  text-align: left;

  @media ${device.mobile} {
    grid-template-columns: 1fr;
  }
`;

const EmptyStateFeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(44, 95, 45, 0.1);
    border-color: var(--color-primary);
  }

  .anticon {
    font-size: 1.25rem;
    color: var(--color-primary);
    flex-shrink: 0;
  }

  span {
    font-family: var(--font-secondary);
    font-size: 0.95rem;
    color: #475569;
    font-weight: 500;
  }
`;

// Today Empty State Styled Components
const TodayEmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 16px;
  border: 2px dashed #e2e8f0;
  margin: 1rem 0;
`;

const TodayEmptyIcon = styled.div`
  font-size: 2.5rem;
  color: #94a3b8;
  margin-bottom: 1rem;
  opacity: 0.7;
`;

const TodayEmptyTitle = styled.h3`
  font-family: var(--font-primary);
  font-size: 1.25rem;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 0.75rem;
`;

const TodayEmptyDescription = styled.p`
  font-family: var(--font-secondary);
  font-size: 0.95rem;
  color: #94a3b8;
  line-height: 1.5;
  max-width: 400px;
  margin-bottom: 1.5rem;
`;

const TodayEmptyAction = styled.div`
  display: flex;
  justify-content: center;
`;

const TodayEmptyButton = styled(Button)`
  background: var(--gradient-primary) !important;
  border: none !important;
  border-radius: 8px !important;
  font-family: var(--font-secondary) !important;
  font-weight: 600 !important;
  font-size: 0.9rem !important;
  height: 42px !important;
  padding: 0 1.5rem !important;
  box-shadow: 0 2px 8px rgba(44, 95, 45, 0.2) !important;
  display: flex !important;
  align-items: center !important;
  gap: 0.5rem !important;

  &:hover {
    transform: translateY(-1px) !important;
    box-shadow: 0 4px 12px rgba(44, 95, 45, 0.3) !important;
  }

  span {
    filter: brightness(0) invert(1);
  }
`;

export default AIAutomationDashboard;

// --- Styled components for Leo Guide Modal ---

const GuideOverlay = styled.div`
  @media (prefers-reduced-motion: no-preference) {
    animation: ${keyframes`from{opacity:0} to{opacity:1}`} 160ms ease-out;
  }
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const GuideModal = styled.div`
  @media (prefers-reduced-motion: no-preference) {
    animation: ${keyframes`from{opacity:0; transform: translateY(8px) scale(.98)} to{opacity:1; transform: translateY(0) scale(1)}`}
      180ms cubic-bezier(0.2, 0.8, 0.2, 1);
  }
  background: white;
  color: var(--color-text);
  border-radius: 12px;
  border: none;
  width: 700px;
  max-width: 100%;
  max-height: 85vh;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;

  @media ${device.mobile} {
    border-radius: 12px;
    width: 100%;
    max-height: 90vh;
  }
`;

const GuideHeader = styled.div`
  padding: 16px 20px;
  border-bottom: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, var(--color-primary) 0%, #4a7c59 100%);
  color: white;
  border-radius: 12px 12px 0 0;
`;

const GuideHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const GuideHeaderIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin-right: -4px;

  .anticon {
    font-size: 20px;
    color: white !important;
  }
`;

const GuideTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  font-family: var(--font-primary);
  color: white;
  line-height: 1.2;
`;

const GuideClose = styled.button`
  background: transparent;
  border: none;
  font-size: 16px;
  color: white;
  cursor: pointer;
  line-height: 1;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const GuideBody = styled.div`
  padding: 24px;
  display: grid;
  gap: 20px;
  background: #fafafa;
  overflow-y: auto;
  flex: 1;

  @media ${device.mobile} {
    padding: 16px;
    gap: 16px;
  }
`;

const GuideHero = styled.div`
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 8px 24px rgba(44, 95, 45, 0.08);

  @media ${device.mobile} {
    padding: 20px;
  }
`;

const HeroIcon = styled.div`
  width: 90px;
  height: 90px;
  border-radius: 24px;
  background: linear-gradient(135deg, var(--color-primary) 0%, #4a7c59 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
  box-shadow: 0 10px 24px rgba(44, 95, 45, 0.25);

  .anticon {
    font-size: 44px;
    color: white !important;
  }
`;

const HeroContent = styled.div`
  flex: 1;
  min-width: 240px;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const HeroTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #0f172a;
  font-family: var(--font-primary);
`;

const HeroSubtitle = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: #475569;
  font-family: var(--font-secondary);
`;

const HeroHighlights = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media ${device.mobile} {
    grid-template-columns: 1fr;
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const HeroHighlight = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  background: #f8fafc;
  border-radius: 10px;
  padding: 16px;
  border: 1px solid #e2e8f0;
  text-align: center;
`;

const HighlightIcon = styled.span`
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: ${({ $bg }) => $bg || "#e6f4f1"};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.08);

  .anticon {
    font-size: 18px;
    color: white !important;
  }
`;

const HighlightText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
  text-align: center;
`;

const HighlightTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
`;

const HighlightCopy = styled.div`
  font-size: 13px;
  color: #475569;
  line-height: 1.5;
`;

const GuideSection = styled.section`
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 16px 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

const GuideSectionTitle = styled.h4`
  margin: 0 0 12px 0;
  font-size: 15px;
  font-weight: 600;
  color: var(--color-text);
  align-items: center;
  gap: 8px;
  font-family: var(--font-primary);

  .anticon {
    color: var(--color-primary);
    font-size: 16px;
  }
`;

const GuideList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 12px;
  line-height: 1.6;
  color: #1e293b;
  font-size: 14px;

  li {
    display: flex;
    gap: 12px;
    align-items: flex-start;
    text-align: left;
  }

  li > span:not(.anticon) {
    flex: 1;
    min-width: 0;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
`;

const BulletIcon = styled.span`
  width: 24px !important;
  min-width: 24px !important;
  max-width: 24px !important;
  height: 24px;
  border-radius: 50%;
  background: ${({ $bg }) => $bg || "#e6f4f1"};
  color: ${({ $color }) => $color || "#2c5f2d"};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0 !important;
  flex-grow: 0 !important;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.08);

  .anticon {
    font-size: 14px;
    color: ${({ $color }) => $color || "#2c5f2d"} !important;
  }
`;

const ChipsGrid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
`;

const Chip = styled.div`
  padding: 14px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
  color: #1e293b;
  font-size: 13px;
  line-height: 1.5;
  transition: all 0.2s ease;
  cursor: pointer;
  display: flex;
  gap: 12px;
  align-items: flex-start;

  &:hover {
    border-color: var(--color-primary);
    background: #f0f7ff;
    box-shadow: 0 2px 8px rgba(44, 95, 45, 0.1);
  }

  > span:not(.anticon) {
    flex: 1;
    min-width: 0;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
`;

const ChipIcon = styled.span`
  width: 28px;
  min-width: 28px;
  height: 28px;
  border-radius: 8px;
  background: ${({ $bg }) => $bg || "#e6f4f1"};
  color: ${({ $color }) => $color || "#2c5f2d"};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.08);

  .anticon {
    font-size: 16px;
  }
`;

const GuideActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin: auto;
  margin-top: 8px;
  margin-bottom: 12px;
`;

const GuideSecondary = styled.button`
  background: var(--color-primary);
  border: 1px solid var(--color-primary);
  border-radius: 8px;
  padding: 10px 20px;
  font-weight: 600;
  font-size: 14px;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #4a7c59;
    border-color: #4a7c59;
    box-shadow: 0 4px 12px rgba(44, 95, 45, 0.2);
  }
`;

const LeoInfoButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  padding: 8px;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  margin-right: 20px;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.5);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;
